import { useAuthHttp } from '@hooks';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { AxiosResponse } from 'axios';
import { CREATE_DESPATCH_NOTE, GET_SKU_URL } from '@constants';
import {
  CreateOutboundShipmentNoteFormValues,
  PreSelectedSku,
} from '@forms/CreateOutboundShipmentNoteForm/types';
import { CreateOutboundShipmentNoteForm } from '@forms/CreateOutboundShipmentNoteForm';
import { SelectedSKU } from '@forms/SKUForm/types';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { toUtcZonedDateTime } from '@utils';

export function CreateOutboundShipmentNote() {
  useDocumentTitle('FLOX - Create Outbound Shipment Note');
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedInventorySkuId = searchParams.get('inventorySkuId');
  const requestedUnit: 'product' | 'sku' =
    searchParams.get('unit') === 'sku' ? 'sku' : 'product';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isLoadingPreSelectedSku, setIsLoadingPreSelectedSku] = useState(
    Boolean(preSelectedInventorySkuId)
  );
  const [preSelectedUnit, setPreSelectedUnit] = useState<'product' | 'sku'>(
    preSelectedInventorySkuId ? requestedUnit : 'sku'
  );
  const [preSelectedSku, setPreSelectedSku] = useState<PreSelectedSku>();

  const fetchPreSelectedSku = async () => {
    try {
      const { data }: AxiosResponse<SelectedSKU> = await authHttp.get(
        GET_SKU_URL,
        { params: { skuId: preSelectedInventorySkuId } }
      );
      const { sku } = data;
      setPreSelectedSku({
        skuId: sku.skuId,
        name: sku.name,
        inventorySkuId: sku.inventorySkuId,
        unitOfMeasure: sku.unitOfMeasure,
        amountOfProductInSku: sku.amountOfProductInSku,
        palletising: sku.palletising,
        stockByWarehouse: sku.stockByWarehouse,
        dimensions: sku.dimensions,
        grossWeight: sku.grossWeight,
        grossWeightUnitOfMeasure: sku.grossWeightUnitOfMeasure,
        images: sku.images,
        productDetails: sku.productDetails
          ? {
              productId: sku.productDetails.productId,
              name: sku.productDetails.name,
              brand: sku.productDetails.brand,
              category: sku.productDetails.category,
              amountOfProductInSku: sku.productDetails.amountOfProductInSku,
              netVolume: sku.productDetails.netVolume,
              netVolumeUnitOfMeasure: sku.productDetails.netVolumeUnitOfMeasure,
              image: sku.productDetails.image,
            }
          : undefined,
      });
      if (requestedUnit === 'product' && !sku.productDetails?.productId) {
        setPreSelectedUnit('sku');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsLoadingPreSelectedSku(false);
    }
  };

  const pages = [
    { name: 'Home', href: '/', current: false },
    {
      name: 'Outbound Shipment Notes',
      href: '/shipments',
      current: false,
    },
    {
      name: 'Create Despatch Note',
      href: '/despatch-note/create',
      current: true,
    },
  ];

  const handleOnSubmit = async (data: CreateOutboundShipmentNoteFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);

    const isFiniteQuantityOfAtLeastOne = (quantity: number) =>
      Number.isFinite(quantity) && quantity >= 1;

    const completeSkus = data.skuFields
      .map((sku) => ({
        skuId: sku.id,
        skuQuantity: Number(sku.skuQuantity),
        palletising: {
          palletisingId: sku.palletising?.palletisingId?.value || null,
        },
      }))
      .filter(
        (sku) => sku.skuId && isFiniteQuantityOfAtLeastOne(sku.skuQuantity)
      );

    const completeProducts = data.productFields
      .map((product) => ({
        productId: product.productId,
        productQuantity: Number(product.productQuantity),
      }))
      .filter(
        (product) =>
          product.productId &&
          isFiniteQuantityOfAtLeastOne(product.productQuantity)
      );

    const payload = {
      purchaseOrderNumber: data.purchaseOrderNumber,
      requiredTimeOfArrival: toUtcZonedDateTime(data.requiredTimeOfDespatch),
      customerId: data.customerId,
      warehouseId: data.warehouseId,
      despatchNoteNumber: data.despatchNoteNumber,
      comment: data.comment,
      skus: completeSkus,
      products: completeProducts,
    };

    try {
      const { data: createOutboundData } = await authHttp.post(
        CREATE_DESPATCH_NOTE,
        {
          despatchNote: {
            ...payload,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (createOutboundData) {
        navigate('/noticeboard');
      }
    } catch (error: any) {
      setSubmitError(
        error?.response?.data?.message ||
          error?.response?.data?.title ||
          error?.response?.data?.errors?.[0]?.message ||
          'Could not create the despatch note. Please review the lines and try again.'
      );
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (preSelectedInventorySkuId) {
      fetchPreSelectedSku();
    }
  }, []);

  if (isLoadingPreSelectedSku) {
    return (
      <div className='flex flex-row justify-center items-center mt-12'>
        <FloxSpinner size='lg' />
      </div>
    );
  }

  return (
    <CreateOutboundShipmentNoteForm
      onSubmit={handleOnSubmit}
      isSubmitting={isSubmitting}
      header='Create Despatch Note'
      pages={pages}
      preSelectedSku={preSelectedSku}
      preSelectedUnit={preSelectedUnit}
      submitError={submitError}
      onDismissError={() => setSubmitError(null)}
    />
  );
}
